import React, { useEffect, useLayoutEffect, useRef } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import ProjectCard from "./ProjectCard";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { prefersReducedMotion, EASE } from "../utils/motion";

gsap.registerPlugin(ScrollTrigger);

const ProjectCarousel = ({ title, projects, onOpenModal, id }) => {
  const sectionRef = useRef(null);
  const carouselRef = useRef(null);
  const thumbRef = useRef(null);
  const leftArrowRef = useRef(null);
  const rightArrowRef = useRef(null);
  const rafRef = useRef(null);

  // ── Update scrollbar + arrow visibility ──────────────────────────────────
  const syncUI = () => {
    const el = carouselRef.current;
    const thumb = thumbRef.current;
    if (!el || !thumb) return;

    const scrollableWidth = el.scrollWidth - el.clientWidth;
    const progress = scrollableWidth > 0 ? (el.scrollLeft / scrollableWidth) * 100 : 0;

    gsap.to(thumb, { width: `${progress}%`, duration: 0.3, ease: "power2.out" });

    // Update arrow visibility via direct DOM (avoids state re-renders on every scroll)
    if (leftArrowRef.current) {
      const canLeft = el.scrollLeft > 2;
      leftArrowRef.current.style.opacity = canLeft ? "1" : "0";
      leftArrowRef.current.style.pointerEvents = canLeft ? "auto" : "none";
      leftArrowRef.current.setAttribute("aria-hidden", canLeft ? "false" : "true");
    }
    if (rightArrowRef.current) {
      const canRight = el.scrollLeft < scrollableWidth - 2;
      rightArrowRef.current.style.opacity = canRight ? "1" : "0";
      rightArrowRef.current.style.pointerEvents = canRight ? "auto" : "none";
      rightArrowRef.current.setAttribute("aria-hidden", canRight ? "false" : "true");
    }
  };

  const handleScroll = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(syncUI);
  };

  // ── Wire up scroll listener ────────────────────────────────────────────
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;

    el.addEventListener("scroll", handleScroll, { passive: true });
    // Set initial arrow states after paint
    const rafId = requestAnimationFrame(syncUI);

    return () => {
      el.removeEventListener("scroll", handleScroll);
      cancelAnimationFrame(rafId);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  // ── Wheel → horizontal scroll (desktop only) ─────────────────────────
  // Lenis intercepts all wheel events for the page; we reclaim them inside
  // the carousel boundary so mouse-wheel users can scroll cards naturally.
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;

    const handleWheel = (e) => {
      const atStart = el.scrollLeft <= 2;
      const atEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 2;

      // At a boundary → let Lenis scroll the page
      if ((e.deltaY < 0 && atStart) || (e.deltaY > 0 && atEnd)) return;

      // Inside the carousel → take over horizontal scroll
      e.preventDefault();
      e.stopPropagation();
      el.scrollLeft += e.deltaY;
    };

    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, []);

  // ── Arrow scroll helpers ───────────────────────────────────────────────
  const SCROLL_STEP = 298; // card width (280) + gap (18)

  const scrollLeft = () =>
    carouselRef.current?.scrollBy({ left: -SCROLL_STEP, behavior: "smooth" });

  const scrollRight = () =>
    carouselRef.current?.scrollBy({ left: SCROLL_STEP, behavior: "smooth" });

  // ── GSAP Animations ───────────────────────────────────────────────────
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const sectionEl  = sectionRef.current;
      const carouselEl = carouselRef.current;
      if (!sectionEl || !carouselEl) return;
      if (prefersReducedMotion()) return;

      const titleEl = sectionEl.querySelector(".carousel-title");
      const cardEls = sectionEl.querySelectorAll(".project-card");

      gsap.from(titleEl, {
        opacity: 0,
        y: 32,
        filter: "blur(6px)",
        duration: 0.85,
        ease: EASE.strong,
        clearProps: "filter",
        scrollTrigger: {
          trigger: sectionEl,
          start: "top 82%",
          toggleActions: "play none none reset",
        },
      });

      gsap.set(cardEls, { opacity: 0, y: 44 });
      ScrollTrigger.batch(cardEls, {
        start: "top 91%",
        onEnter: (batch) => {
          gsap.to(batch, {
            opacity: 1,
            y: 0,
            duration: 0.72,
            stagger: 0.09,
            ease: EASE.standard,
          });
        },
        onLeaveBack: (batch) =>
          gsap.to(batch, { opacity: 0, y: 44, duration: 0.38, stagger: 0.04 }),
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [projects]);

  return (
    <section className="carousel-section" id={id} ref={sectionRef}>
      <h2 className="carousel-title">{title}</h2>

      <div className="carousel-container">
        {/* Left arrow */}
        <button
          ref={leftArrowRef}
          className="carousel-arrow carousel-arrow--left"
          onClick={scrollLeft}
          aria-label="Scroll left"
          aria-hidden="true"
          style={{ opacity: 0, pointerEvents: "none" }}
        >
          <FiChevronLeft />
        </button>

        {/* Scrollable cards */}
        <div className="carousel" ref={carouselRef}>
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onOpenModal={onOpenModal}
            />
          ))}
        </div>

        {/* Right arrow */}
        <button
          ref={rightArrowRef}
          className="carousel-arrow carousel-arrow--right"
          onClick={scrollRight}
          aria-label="Scroll right"
          aria-hidden="true"
          style={{ opacity: 0, pointerEvents: "none" }}
        >
          <FiChevronRight />
        </button>

        {/* Custom scrollbar */}
        <div className="scrollbar-track">
          <div className="scrollbar-thumb" ref={thumbRef}></div>
        </div>
      </div>
    </section>
  );
};

export default React.memo(ProjectCarousel);
